import { useEffect, useState, useCallback } from "react";
import { Button, Input, Typography, Skeleton, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { api } from "../../services/api";
import { UsuariosCliente } from "../../shared/components/UsuariosCliente";
import { DomiciliosCliente } from "../../shared/components/DomiciliosCliente";

const { Text } = Typography;

const vacio = { nombre: "", dni: "", cuit: "", telefono: "", domicilio: "" };

// MiCliente son los datos con los que el usuario compra (su cliente) y las
// personas que compran con él. Una empresa puede sumar a sus compañeros desde
// acá, sin pasar por la tienda.
export const MiCliente = ({ user }) => {
  const [cliente, setCliente] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [domicilios, setDomicilios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(vacio);
  const [guardando, setGuardando] = useState(false);

  const cargarUsuarios = useCallback(async () => {
    const res = await api.clientes.misUsuarios();
    if (res.status === "success") setUsuarios(res.data || []);
  }, []);

  const cargarDomicilios = useCallback(async () => {
    const res = await api.clientes.misDomicilios();
    if (res.status === "success") setDomicilios(res.data || []);
  }, []);

  useEffect(() => {
    (async () => {
      const res = await api.clientes.mio();
      if (res.status === "success" && res.data) {
        setCliente(res.data);
        await Promise.all([cargarUsuarios(), cargarDomicilios()]);
      }
      setLoading(false);
    })();
  }, [cargarUsuarios, cargarDomicilios]);

  const empezarEdicion = () => {
    setForm({
      nombre: cliente.nombre || "", dni: cliente.dni || "", cuit: cliente.cuit || "",
      telefono: cliente.telefono || "", domicilio: cliente.domicilio || "",
    });
    setEdit(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { message.error("El nombre es obligatorio"); return; }
    setGuardando(true);
    try {
      const res = await api.clientes.actualizar(form);
      if (res.status === "success") {
        setCliente(res.data);
        setEdit(false);
        message.success("Datos guardados");
      } else {
        message.error(res.message || "No se pudieron guardar tus datos");
      }
    } finally {
      setGuardando(false);
    }
  };

  if (loading) return <Skeleton active paragraph={{ rows: 4 }} />;

  if (!cliente) {
    return (
      <div>
        <Text strong style={{ display: "block", marginBottom: 8 }}>Datos de cliente</Text>
        <Text type="secondary">Te los pedimos cuando hagas tu primera compra.</Text>
      </div>
    );
  }

  const deERP = !!cliente.idcliente_erp;
  const campo = (label, valor) => (
    <Text type="secondary">{label}: <strong>{valor || "-"}</strong></Text>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <Text strong>Datos de cliente</Text>
          {!edit && !deERP && (
            <Button type="text" icon={<EditOutlined style={{ color: "var(--color-primary)" }} />}
              onClick={empezarEdicion} style={{ color: "var(--color-primary)" }}>
              Editar
            </Button>
          )}
        </div>

        {edit ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Input placeholder="Nombre o razón social" value={form.nombre}
              onChange={(e) => setForm(p => ({ ...p, nombre: e.target.value }))} />
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Input placeholder="DNI" inputMode="numeric" value={form.dni} style={{ flex: "1 1 140px" }}
                onChange={(e) => setForm(p => ({ ...p, dni: e.target.value }))} />
              <Input placeholder="CUIT" value={form.cuit} style={{ flex: "1 1 140px" }}
                onChange={(e) => setForm(p => ({ ...p, cuit: e.target.value }))} />
            </div>
            <Input placeholder="Teléfono" value={form.telefono}
              onChange={(e) => setForm(p => ({ ...p, telefono: e.target.value }))} />
            <Input placeholder="Domicilio" value={form.domicilio}
              onChange={(e) => setForm(p => ({ ...p, domicilio: e.target.value }))} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button onClick={() => setEdit(false)}>Cancelar</Button>
              <Button type="primary" loading={guardando} onClick={guardar}>Guardar</Button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {campo("Nombre", cliente.nombre)}
            {campo("DNI", cliente.dni)}
            {campo("CUIT", cliente.cuit)}
            {campo("Teléfono", cliente.telefono)}
            {campo("Domicilio", cliente.domicilio)}
            {campo("Mail de avisos", cliente.mail)}
            {deERP && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Estos datos los administra la tienda. Para cambiarlos, escribile.
              </Text>
            )}
          </div>
        )}
      </div>

      <div>
        <Text strong style={{ display: "block", marginBottom: 12 }}>Mis domicilios</Text>
        <DomiciliosCliente
          domicilios={domicilios}
          sugerido={{ nombre: user.nombre || "", apellido: user.apellido || "", dni: cliente.dni || "", telefono_contacto: user.telefono || cliente.telefono || "" }}
          crear={async (data) => {
            const res = await api.clientes.crearDomicilio(data);
            if (res.status === "success") await cargarDomicilios();
            return res;
          }}
          editar={async (did, data) => {
            const res = await api.clientes.editarDomicilio(did, data);
            if (res.status === "success") await cargarDomicilios();
            return res;
          }}
          quitar={async (did) => {
            const res = await api.clientes.quitarDomicilio(did);
            if (res.status === "success") await cargarDomicilios();
            return res;
          }}
        />
      </div>

      <div>
        <Text strong style={{ display: "block", marginBottom: 12 }}>Usuarios de la empresa</Text>
        <UsuariosCliente
          usuarios={usuarios}
          ayuda="Si comprás para una empresa, sumá a tus compañeros: van a ver los mismos pedidos y comprar con los mismos precios."
          esUnoMismo={(u) => u.id === user.id}
          agregar={async (mail) => {
            const res = await api.clientes.agregarUsuario(mail);
            if (res.status === "success") await cargarUsuarios();
            return res;
          }}
          quitar={async (uid) => {
            const res = await api.clientes.quitarUsuario(uid);
            if (res.status === "success") await cargarUsuarios();
            return res;
          }}
        />
      </div>
    </div>
  );
};
